import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { gql } from "__generated__/gql";
import { useLazyQuery } from "@apollo/client";

import { PageHeader, Card } from "@cofano/cck";
import { Grid, Tabs, Tab, Alert, CircularProgress } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AssignmentLate } from "@mui/icons-material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import PeriodField, { getPeriodWindow } from "components/form/PeriodField";
import { Language, hasPluginPermission } from "imports/Utils";
import { useStoreState } from "ezStore";
import PurchaseCharters from "./PurchaseCharters";
import SalesCharters from "./SalesCharters";
import DeclarationLines from "./DeclarationLines";
import InvoiceLines from "./Invoicelines";
import Reports from "./SailingReports";
import Remark from "./Remark";
import { CharterSelector } from "components/declaration/mutualComponents";
import Helmet from "components/helmet/Helmet";
import { intl } from "LocaleWrapper";
import NonPurchaseShips from "./NonPurchaseShips";
import { DateUtils } from "utils/DateUtils";
import DebtorFilter from "./components/filter/DebtorFilter";
import ScheduleFilter from "./components/filter/ScheduleFilter";

import "./declarations.scss";
import DeclarationViewButtons, { DeclarationView } from "view/declarations/components/buttons/DeclarationViewButtons";
import { SailingReportRangeType } from "view/sailingreports/utils/SailingReportUtils.tsx";

const TABS = {
	DECLARATION: "declaration",
	COST_LINES: "costLines",
};

const Declarations = () => {

	const [ searchParams ] = useSearchParams();
	const navigate = useNavigate();

	const session = useStoreState(state => state.session);
	const period = hasPluginPermission(session, "declarationOverviewMonth") ? SailingReportRangeType.Month : SailingReportRangeType.Week;

	const [ state, setState ] = useState({
		charter: undefined,
		loading: false,
		collapsed: false,
		collapsed_ships_without_purchase: true,
		selectedTab: TABS.DECLARATION,
		selectedCharterId: searchParams.get("charter") ? Number(searchParams.get("charter")) : undefined,
		selectableCharters: [],
		updatePurchaseChartersRequestId: 0,
		updateNonPurchaseShipRequestId: 0,
		window: getPeriodWindow(searchParams.get("from") ? Number(searchParams.get("from")) : undefined, period),
	});
	const [ hasNonPurchaseShips, setHasNonPurchaseShips ] = useState(false);
	const [ debtors, setDebtors ] = useState([]);
	const [ selectedSchedule, setSelectedSchedule ] = useState(null);

	const didMount = useRef(false);

	const onGetCharterQueryComplete = (data) => {
		const charter = data.charter;
		if (charter?.storedValidUntil <= DateUtils.getTimestamp(state.window.from)
			|| charter?.storedValidFrom >= DateUtils.getTimestamp(state.window.to)) {
			setState(oldState => ({
				...oldState,
				loading: false,
				charter: undefined,
				selectedTab: TABS.DECLARATION,
				selectableCharters: [],
				selectedCharterId: undefined,
			}));
		} else {
			setState(oldState => ({
				...oldState,
				loading: false,
				charter: data.charter,
				selectedTab: data.charter?.declarations?.[0] ? TABS.COST_LINES : TABS.DECLARATION,
			}));
		}
	};

	const [ getCharter ] = useLazyQuery(CHARTER_QUERY, {
		onError: () => {
			setState(oldState => ({
				...oldState,
				loading: false,
				charter: undefined,
			}));
		},
	});

	const fetchCharter = (ignoreResult = false, id) => {
		getCharter({
			variables: {
				id: id || state.charter?.id,
				from: moment(state.window.from).unix() * 1000,
				to: moment(state.window.to).unix() * 1000,
				onlyDeclarableAgreements: true,
			},
		}).then(response => {
			if (!ignoreResult) {
				onGetCharterQueryComplete(response.data);
			}
		});
	};

	const handleWindowChange = (newWindow) => setState(oldState => ({
		...oldState,
		window: {
			from: newWindow.from,
			to: newWindow.to,
		},
	}));

	const handleUrl = () => {
		const from = moment(state.window.from).unix() * 1000;
		const to = moment(state.window.to).unix() * 1000;
		navigate(`/view/declarations/purchase?${state.selectedCharterId ? `charter=${state.selectedCharterId}&` : ""}from=${from}&to=${to}`);
	};

	const handleSelectCharter = (id, ignoreResult = false) => {
		setState(oldState => ({
			...oldState,
			loading: !!id,
			selectedCharterId: id,
		}));

		if (id) {
			fetchCharter(ignoreResult, id);
		}
	};

	const actuallyUpdatePurchaseCharterRequestId = () => setState({ ...state, updatePurchaseChartersRequestId: state.updatePurchaseChartersRequestId + 1 });

	const setLoading = (loadingState) => {
		setState(oldState => ({
			...oldState,
			loading: loadingState,
		}));
	};

	useEffect(() => {
		handleUrl();
	}, [ state.selectedCharterId, state.window ]);


	useEffect(() => {
		let ignoreQuery = false;
		if (didMount.current) {
			handleSelectCharter(state.charter?.id, ignoreQuery);
		}

		return () => {
			ignoreQuery = true;
		};
	}, [ state.window.from, state.window.to ]);

	useEffect(() => {
		if (!didMount.current) {
			didMount.current = true;
		}

		if (searchParams.get("charter")) {
			handleSelectCharter(Number(searchParams.get("charter")));
		}
	}, []);

	return (
		<div className="declarations-page">
			<PageHeader>
				<PageHeader.Title>
					{Language("PURCHASE_DECLARATIONS")}
					<Helmet title={intl.formatMessage({ id: "PURCHASE_DECLARATIONS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<div className={"declaration-filters"}>
						<Card disablePadding>
							<div className="declaration-filters-fields">
								<ScheduleFilter onSelectSchedule={(value) => {
									setSelectedSchedule(value);
									setState(oldState => ({
										...oldState,
										selectedCharterId: undefined,
										charter: undefined,
									}));
								}} />
								<DebtorFilter onSelectDebtors={(values) => {
									setDebtors(values);
									setState(oldState => ({
										...oldState,
										selectedCharterId: undefined,
										charter: undefined,
									}));
								}} />

								<CharterSelector
									disabled={state.loading}
									selectedCharterId={state.selectedCharterId}
									currentCharters={state.selectableCharters}
									onChange={charter => handleSelectCharter(charter.purchaseCharter?.id)}
									renderField={(charter) => charter.ship?.name || Language("UNKNOWN")} />

								<PeriodField
									period={period}
									disabled={state.loading}
									defaultStart={state.window.from}
									onChange={handleWindowChange} />
							</div>
						</Card>
					</div>

					<DeclarationViewButtons active={DeclarationView.Time} />
				</PageHeader.Actions>
			</PageHeader>

			<div className={`declaration-container ${state.collapsed ? "collapsed" : ""}`}>
				<div className="sidebar">
					<div className="sidebar-content">
						<div className="sidebar-content-opacity">
							<div className="purchase-charters">
								<PurchaseCharters
									key={cantorPair(state.window.from, state.window.to)}
									selectedCharterId={state.selectedCharterId}
									onSelect={purchaseCharter => handleSelectCharter(purchaseCharter.id)}
									onChange={charters => {
										setState(oldState => ({
											...oldState,
											selectableCharters: charters,
										}));
									}}
									setLoading={setLoading}
									updatePurchaseChartersRequestId={state.updatePurchaseChartersRequestId}
									actuallyUpdatePurchaseCharterRequestId={actuallyUpdatePurchaseCharterRequestId}
									window={state.window}
									debtors={debtors}
									selectedSchedule={selectedSchedule}
								/>
							</div>
							<div className="divider">
								<h3>{Language("SHIPS_WITHOUT_PURCHASES")} {hasNonPurchaseShips ? <span className="icon"><AssignmentLate /></span> : <></>}</h3>
								<CollapseButton
									// Since we're collapsing to the bottom, we want the arrow to point up when collapsed, so we invert the logic here
									collapsed={!state.collapsed_ships_without_purchase}
									vertical
									onClick={() => {
										setState(oldState => ({
											...oldState,
											collapsed_ships_without_purchase: !state.collapsed_ships_without_purchase,
										}));
									}} />
							</div>
							<div className={`ships-without-purchase ${state.collapsed_ships_without_purchase ? "collapsed" : ""}`}>
								<NonPurchaseShips
									key={cantorPair(state.window.from, state.window.to)}
									onHasNonPurchaseShips={setHasNonPurchaseShips}
									onSelect={purchaseCharter => handleSelectCharter(purchaseCharter.id)}
									onChange={charters => {
										setState(oldState => ({
											...oldState,
											selectableCharters: charters,
										}));
									}}
									updateNonPurchaseShipRequestId={state.updateNonPurchaseShipRequestId}
									window={state.window}
								/>
							</div>
						</div>
						<CollapseButton
							collapsed={state.collapsed}
							onClick={() => {
								setState(oldState => ({
									...oldState,
									collapsed: !state.collapsed,
								}));
							}} />
					</div>
				</div>

				<div className="content">
					{(state.charter) ? (
						<React.Fragment>
							<div style={{ marginBottom: 16 }}>
								<Card disablePadding>
									<div className="sales-charters">
										<SalesCharters
											charter={state.charter}
											loading={state.loading}
											refetch={() => {
												setState(oldState => ({
													...oldState,
													loading: true,
												}));

												fetchCharter();
											}} />
									</div>
								</Card>
							</div>

							<Grid spacing={3} container>
								<Grid xs={12} item>
									<Card disablePadding>
										<Tabs
											value={state.selectedTab}
											onChange={(event, newValue) => {
												setState(oldState => ({
													...oldState,
													selectedTab: newValue,
												}));
											}}>
											<Tab label={Language("DECLARATION")} value={TABS.DECLARATION} />
											<Tab label={Language("COST_LINES")} value={TABS.COST_LINES} />
										</Tabs>
									</Card>
								</Grid>

								<Grid xs={12} item>
									<div style={{ display: "flex" }}>
										<div className="content-cost-lines">
											<Card>
												{state.selectedTab === TABS.DECLARATION && (
													<DeclarationLines
														charterId={state.charter.id}
														window={state.window}
														refetch={() => {
															setState(oldState => ({
																...oldState,
																loading: true,
																selectedTab: TABS.COST_LINES,
																updatePurchaseChartersRequestId: oldState.updatePurchaseChartersRequestId + 1,
																updateNonPurchaseShipRequestId: oldState.updateNonPurchaseShipRequestId + 1,
															}));

															fetchCharter();
														}} />
												)}

												{state.selectedTab === TABS.COST_LINES && (
													<InvoiceLines
														charter={state.charter}
														loading={state.loading}
														window={state.window}
														deletable
														refetch={() => {
															setState(oldState => ({
																...oldState,
																loading: true,
																updatePurchaseChartersRequestId: oldState.updatePurchaseChartersRequestId + 1,
																updateNonPurchaseShipRequestId: oldState.updateNonPurchaseShipRequestId + 1,
															}));

															fetchCharter();
														}} />
												)}
											</Card>
										</div>

										<div className="content-revenue">
											<div style={{ marginBottom: 16 }}>
												<Card>
													{state.loading ? <CircularProgress size={24} /> :
														<Reports
															charter={state.charter}
															window={state.window}
															refetch={fetchCharter} />}
												</Card>
											</div>

											<Card>
												<Remark
													charter={state.charter}
													window={state.window}
													refetch={fetchCharter} />
											</Card>
										</div>
									</div>
								</Grid>
							</Grid>
						</React.Fragment>
					) : (
						<React.Fragment>
							<Alert severity="warning">{Language("SELECT_PURCHASE_CHARTER")}</Alert>
						</React.Fragment>
					)}
				</div>
			</div>
		</div>
	);
};

export const CollapseButton = ({ collapsed, onClick, vertical }) => {
	return (
		<div className="collapse-button" onClick={() => onClick()}>
			{vertical ? (
				<React.Fragment>
					{collapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
				</React.Fragment>
			) : (
				<React.Fragment>
					{collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowLeftIcon />}
				</React.Fragment>
			)}
		</div>
	);
};

export const normalizeUnit = (unit) => {
	if (!unit || unit === "NONE") {
		return "";
	}

	if (unit === "MTON") {
		return intl.formatMessage({ id: "CARGO_QUANTITY_UNIT_MTON" });
	}

	if (unit === "M3") {
		return intl.formatMessage({ id: "CARGO_QUANTITY_UNIT_M3" });
	}

	if (unit === "HOUR") {
		return intl.formatMessage({ id: "HOURS" });
	}

	if (unit === "DAY_PRO_RATA" || unit === "DAY") {
		return intl.formatMessage({ id: "DAYS" });
	}

	if (unit === "REEFER") {
		return intl.formatMessage({ id: "REEFERS" });
	}

	return intl.formatMessage({ id: unit });
};

/**
 * Implements the [Cantor pairing function](https://en.wikipedia.org/wiki/Pairing_function).
 * @param {number} x The first number.
 * @param {number} y The second number.
 * @returns {number} A unique number representing the pair.
 */
export const cantorPair = (x, y) =>
	(x + y) * (x + y + 1) / 2 + y;

export const CHARTER_QUERY = gql(`#graphql
	query charter_declarations($id: Long!, $from: Instant, $to: Instant, $onlyDeclarableAgreements : Boolean) {
		charter(id: $id) {
			id
			charterNumber
			creditor {
				id
				name
			}
			type
			... on PurchaseCharter {
				ships {
					id
					name
				}
				voyages(from: $from, to: $to, excludeEmpty: true) {
					id
					voyageNumber
					from {
						id
						description
						code
						displayCode
						locationGroups {
							type
							description
							code
							displayCode
						}
					}
					to {
						id
						description
						code
						displayCode
						locationGroups {
							type
							description
							code
							displayCode
						}
					}
					start
					end
					administrativeTimestamp
					cargoRevenue(from: $from, to: $to)
					voyageRevenue(from: $from, to: $to)
					revenue(from: $from, to: $to)
					ship {
						id
					}
					calls {
						id
						location {
							id
							code
							displayCode
						}
						pta
						callLoadingTeu
						callDischargingTeu
						comments {
							id
							comment
							created
							createdBy {
								username
							}
						}
						loadedWeight
						dischargedWeight
					}
					voyageMetrics {
						loadedTeu
						loadedTeuPercentage
						loadedTon
						loadedTonPercentage
					}
					allocatedInvoicelines {
						id
						approved
						quantity
						unexpected
						price {
							value
							currency {
								symbol
								isoCode
							}
						}
					}
					schedule {
						id
						schedule
					}
				}
			}
			declarations(from: $from, to: $to) {
				id
				createdOn
				correction
				invoiceGroupType {
					id
					name
				}
				declarationValues {
					id
					agreement {
						id
						summary
						description
						priority
						calculateAmount(charterId: $id, from: $from, to: $to)
					}
					quantity
					price
				}
				remark
				closedOn
				financiallyReadiedOn
				invoiceLines {
					id
					approved
					invoiceReference
					description
					quantity
					unit
					totalPriceExcVAT
					price {
						value
						currency {
							isoCode
							rate
							symbol
						}
					}
					creditor {
						id
						name
					}
					debtor {
						id
						name
					}
					costType {
						id
						title_en
						title_nl
						title_de
					}
					agreement {
						id
					}
					linkedDeclarationValue {
						id
						agreement {
							id
						}
						quantity
						price
					}
					unexpected
				}
			}
			contract {
				id
				contractNumber
				validFrom
				validUntil
				isDeclarable
				agreements(onlyDeclarableAgreements : $onlyDeclarableAgreements, charterId : $id) {
					id
					calculateAmount(charterId: $id, from: $from, to: $to)
					hasFixedAmount
					mayOverridePrice
					summary
					description
					priority
					type
					invoiceGroupType {
						id
						name
					}
					costType {
						id
						title_en
						title_nl
						title_de
					}
					calculationMethod {
						id
						type
						price
						minimumPrice
						minimumQuantityPayable
						preAgreed
						payableOver
						description
						unit
						customUnit
					}
				}
			}
		}
	}
`);

export default Declarations;
